import { useCallback } from "react"
import { apiFetch } from "../../../utils/apiFetch"

const FDI_PERMANENT_TEETH = new Set([
  18, 17, 16, 15, 14, 13, 12, 11,
  21, 22, 23, 24, 25, 26, 27, 28,
  48, 47, 46, 45, 44, 43, 42, 41,
  31, 32, 33, 34, 35, 36, 37, 38,
])

const VALID_SURFACES = new Set(["ALL", "M", "O", "D", "V", "L"])

function normalizeFdiTooth(value) {
  if (value === undefined || value === null || value === "") return null

  const parsed = Number(value)
  if (!Number.isInteger(parsed) || !FDI_PERMANENT_TEETH.has(parsed)) return null

  return parsed
}

function hasToothValue(value) {
  return value !== undefined && value !== null && String(value).trim() !== ""
}

function normalizeSurface(value) {
  const raw = String(value || "ALL").trim().toUpperCase()
  if (!VALID_SURFACES.has(raw)) return "ALL"
  return raw
}

function parseMoneyInput(value) {
  const normalized = String(value || "")
    .replace(/,/g, ".")
    .replace(/[^0-9.]/g, "")

  const firstDot = normalized.indexOf(".")
  const safe =
    firstDot === -1
      ? normalized
      : normalized.slice(0, firstDot + 1) +
        normalized.slice(firstDot + 1).replace(/\./g, "")

  const parsed = Number(safe)
  return Number.isFinite(parsed) ? parsed : 0
}

export default function useAgendaClinicalActions({
  API_BASE,
  clinicId,
  date,
  selectedAppointment,
  selectedAppointmentTreatmentId,
  treatmentOdontogramForm,
  setCheckingIn,
  setRegisteringTreatment,
  setSelectedAppointment,
  loadHeldSlots,
  loadAgenda,
  loadFinancialSummary,
  loadPatientSnapshot,
  loadPatientOdontogram,
  resetTreatmentOdontogramForm,
  onPlanItemLinked,
  isReadOnly,
  readOnlyReason
}) {
  const refreshAgendaView = useCallback(
    async (targetClinicId = clinicId, targetDate = date) => {
      if (!targetClinicId) return

      const freshHeld = await loadHeldSlots(targetClinicId, targetDate)
      await loadAgenda(targetClinicId, targetDate, freshHeld)
    },
    [clinicId, date, loadHeldSlots, loadAgenda]
  )

  const resolveMutationClinicId = useCallback(() => {
    if (clinicId && clinicId !== "ALL") return clinicId
    if (selectedAppointment?.clinicId) return selectedAppointment.clinicId
    if (selectedAppointment?.extendedProps?.clinicId) {
      return selectedAppointment.extendedProps.clinicId
    }
    return ""
  }, [clinicId, selectedAppointment])

  const checkInSelectedAppointment = useCallback(async () => {
    if (isReadOnly) {
      alert(readOnlyReason || "Modo solo lectura activo")
      return false
    }

    if (!selectedAppointment?.id) return false

    const mutationClinicId = resolveMutationClinicId()

    if (!mutationClinicId) {
      alert(
        clinicId === "ALL"
          ? "Vista consolidada activa. No pude identificar la clínica real de esta cita."
          : "No se pudo identificar la clínica de la cita."
      )
      return false
    }

    try {
      setCheckingIn(true)

      const res = await apiFetch(`${API_BASE}/appointments/checkin-specific`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          clinicId: mutationClinicId,
          appointmentId: selectedAppointment.id
        })
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        alert(data?.message || "No se pudo confirmar la llegada")
        return false
      }

      const checkedInFromResponse =
        data?.checkedIn ??
        data?.appointment?.checkedIn ??
        data?.data?.checkedIn

      const statusFromResponse =
        data?.status ??
        data?.appointment?.status ??
        data?.data?.status

      if (typeof setSelectedAppointment === "function") {
        setSelectedAppointment((prev) =>
          prev
            ? {
                ...prev,
                clinicId: mutationClinicId,
                checkedIn: checkedInFromResponse ?? true,
                checkedInTime:
                  data?.checkedInTime ||
                  data?.appointment?.checkedInTime ||
                  data?.data?.checkedInTime ||
                  new Date().toISOString(),
                status: statusFromResponse || prev.status || "CHECKED_IN",
              }
            : prev
        )
      }

      await refreshAgendaView(clinicId || mutationClinicId, date)
      await loadPatientSnapshot(selectedAppointment.patientId)

      if (
        checkedInFromResponse === false ||
        statusFromResponse === "SCHEDULED"
      ) {
        alert(
          "El endpoint respondió OK, pero la cita no regresó como confirmada. Probablemente el cambio no se está persistiendo en backend."
        )
        return false
      }

      alert("Llegada confirmada")
      return true
    } catch {
      alert("Error al confirmar llegada")
      return false
    } finally {
      setCheckingIn(false)
    }
  }, [
    isReadOnly,
    readOnlyReason,
    API_BASE,
    clinicId,
    date,
    selectedAppointment,
    setCheckingIn,
    setSelectedAppointment,
    refreshAgendaView,
    loadPatientSnapshot,
    resolveMutationClinicId
  ])

  const registerTreatmentForSelectedAppointment = useCallback(async () => {
    if (isReadOnly) {
      alert(readOnlyReason || "Modo solo lectura activo")
      return false
    }

    if (!selectedAppointment?.id) return false

    if (!selectedAppointmentTreatmentId) {
      alert("Selecciona un tratamiento")
      return false
    }

    const mutationClinicId = resolveMutationClinicId()

    if (!mutationClinicId) {
      alert("No se pudo identificar la clínica de la cita.")
      return false
    }

    const rawPrice = treatmentOdontogramForm?.price
    const hasCustomPrice =
      rawPrice !== undefined &&
      rawPrice !== null &&
      String(rawPrice).trim() !== ""

    const priceToCharge = hasCustomPrice ? parseMoneyInput(rawPrice) : undefined

    if (hasCustomPrice && (!Number.isFinite(priceToCharge) || priceToCharge < 0)) {
      alert("El precio a cobrar no es válido")
      return false
    }

    const normalizedTooth = normalizeFdiTooth(treatmentOdontogramForm?.tooth)
    const normalizedSurface = normalizeSurface(treatmentOdontogramForm?.surface)

    if (hasToothValue(treatmentOdontogramForm?.tooth) && !normalizedTooth) {
      alert("Selecciona una pieza dental válida.")
      return false
    }

    try {
      setRegisteringTreatment(true)

      const res = await apiFetch(
        `${API_BASE}/appointments/register-treatment-from-appointment`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            clinicId: mutationClinicId,
            appointmentId: selectedAppointment.id,
            patientId: selectedAppointment.patientId,
            doctorId: selectedAppointment.doctorId,
            treatmentId: selectedAppointmentTreatmentId,
            ...(priceToCharge !== undefined ? { price: priceToCharge } : {}),
            tooth: normalizedTooth,
            surface: normalizedSurface,
            condition: treatmentOdontogramForm?.condition,
            status: treatmentOdontogramForm?.status
          })
        }
      )

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        alert(data?.message || "No se pudo registrar el tratamiento")
        return false
      }

      if (data?.alreadyRegistered) {
        alert("Ese tratamiento ya estaba registrado para esta cita")
      } else {
        alert("Tratamiento registrado correctamente")
      }

      await refreshAgendaView(clinicId || mutationClinicId, date)
      await loadFinancialSummary(selectedAppointment.id, mutationClinicId)
      await loadPatientSnapshot(selectedAppointment.patientId, mutationClinicId)
      await loadPatientOdontogram(selectedAppointment.patientId, mutationClinicId)

      if (typeof setSelectedAppointment === "function") {
        setSelectedAppointment((prev) => {
          if (!prev) return prev

          const currentStatus = String(prev.status || "").toUpperCase()
          const statusFromResponse =
            data?.status ||
            data?.appointment?.status ||
            data?.data?.status

          const fallbackStatus =
            ["CHECKED_IN", "IN_PROGRESS", "CONFIRMED", "SCHEDULED", "BOOKED", "PROGRAMMED"].includes(
              currentStatus
            )
              ? "COMPLETED"
              : prev.status

          return {
            ...prev,
            status: statusFromResponse || fallbackStatus,
            lastTreatmentRegisteredAt: new Date().toISOString(),
          }
        })
      }

      if (onPlanItemLinked) {
        await onPlanItemLinked(selectedAppointment.patientId)
      }

      resetTreatmentOdontogramForm()
      return true
    } catch {
      alert("Error al registrar tratamiento")
      return false
    } finally {
      setRegisteringTreatment(false)
    }
  }, [
    isReadOnly,
    readOnlyReason,
    API_BASE,
    clinicId,
    date,
    selectedAppointment,
    selectedAppointmentTreatmentId,
    treatmentOdontogramForm,
    setRegisteringTreatment,
    setSelectedAppointment,
    refreshAgendaView,
    loadFinancialSummary,
    loadPatientSnapshot,
    loadPatientOdontogram,
    resetTreatmentOdontogramForm,
    onPlanItemLinked,
    resolveMutationClinicId
  ])

  const deleteTreatmentFromSelectedAppointment = useCallback(async (treatmentLogId) => {
    if (isReadOnly) {
      alert(readOnlyReason || "Modo solo lectura activo")
      return false
    }

    if (!selectedAppointment?.id || !treatmentLogId) return false

    const mutationClinicId = resolveMutationClinicId()

    if (!mutationClinicId) {
      alert("No se pudo identificar la clínica de la cita.")
      return false
    }

    try {
      setRegisteringTreatment(true)

      const res = await apiFetch(`${API_BASE}/appointments/delete-treatment-from-appointment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ treatmentLogId })
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        alert(data?.message || "No se pudo eliminar el tratamiento")
        return false
      }

      await refreshAgendaView(clinicId || mutationClinicId, date)
      await loadFinancialSummary(selectedAppointment.id, mutationClinicId)
      await loadPatientSnapshot(selectedAppointment.patientId, mutationClinicId)
      await loadPatientOdontogram(selectedAppointment.patientId, mutationClinicId)

      alert(data?.message || "Tratamiento eliminado correctamente")
      return true
    } catch (error) {
      alert(error?.message || "Error al eliminar el tratamiento")
      return false
    } finally {
      setRegisteringTreatment(false)
    }
  }, [
    isReadOnly,
    readOnlyReason,
    API_BASE,
    clinicId,
    date,
    selectedAppointment,
    setRegisteringTreatment,
    refreshAgendaView,
    loadFinancialSummary,
    loadPatientSnapshot,
    loadPatientOdontogram,
    resolveMutationClinicId
  ])

  return {
    checkInSelectedAppointment,
    registerTreatmentForSelectedAppointment,
    deleteTreatmentFromSelectedAppointment
  }
}